import ApplicableIcon from '@/assets/applicable.svg?react';
import NotApplicableIcon from '@/assets/not_applicable.svg?react';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MarkdownText } from '@/shared/MarkdownText';
import { cn } from '@/utils/classUtils';
import { getSourceIconUrl } from '@/utils/source-icon';
import { ApplicabilityStatus, ListUpdatesResponse } from '@ditto/shared';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Archive,
  CircleCheck,
  CircleDashed,
  CircleX,
  FileText,
} from 'lucide-react';
import { type FC, type MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import { STATUS_LABELS } from '../labels';
import {
  DocumentTypeBadge,
  PriorityDisplay,
  RegulatoryRegionBadge,
  SourceBadge,
  UpdateTypeBadge,
} from './columns';
import {
  DOCUMENT_REFERENCE_BUTTON_CLASS,
  DocumentReferenceButton,
} from './DocumentReferenceButton';
import { NewsfeedRelevantChipsRow } from './NewsfeedRelevantChipsRow';

dayjs.extend(relativeTime);

const formatRelativeDate = (
  dateString: string | null | undefined
): { relative: string; absolute: string } | null => {
  if (!dateString) return null;
  const d = dayjs(dateString);
  return {
    relative: d.fromNow(),
    absolute: d.format('D MMMM YYYY'),
  };
};

const STATUS_ICON_CONFIG: Record<
  string,
  { icon: FC<{ className?: string }>; colorClass: string }
> = {
  to_review: { icon: CircleDashed, colorClass: 'text-neutral-400' },
  applicable: { icon: CircleCheck, colorClass: 'text-green-500' },
  not_applicable: { icon: CircleX, colorClass: 'text-red-500' },
  out_of_scope: { icon: Archive, colorClass: 'text-neutral-400' },
};

const FILLED_STATUS_ICONS: Record<string, FC<{ className?: string }>> = {
  applicable: ApplicableIcon,
  not_applicable: NotApplicableIcon,
};

const StatusIcon: FC<{ status: string | null | undefined }> = ({ status }) => {
  if (!status) return null;
  const FilledIcon = FILLED_STATUS_ICONS[status];
  if (FilledIcon) return <FilledIcon className="h-5 w-5" />;
  const config = STATUS_ICON_CONFIG[status];
  if (!config) return null;
  const Icon = config.icon;
  return <Icon className="h-5 w-5 text-neutral-900" />;
};

const STATUS_OPTIONS: {
  value: ApplicabilityStatus;
  icon: FC<{ className?: string }>;
  colorClass: string;
}[] = [
  { value: 'to_review', icon: CircleDashed, colorClass: 'text-neutral-400' },
  { value: 'applicable', icon: CircleCheck, colorClass: 'text-green-500' },
  { value: 'not_applicable', icon: CircleX, colorClass: 'text-red-500' },
  { value: 'out_of_scope', icon: Archive, colorClass: 'text-neutral-500' },
];

const ImpactStatusBadge: FC<{
  status: ApplicabilityStatus;
  aiImpactStatus: string | null;
  recommendedApplicability: ApplicabilityStatus | null;
}> = ({ status, recommendedApplicability }) => {
  // When status is to_review and AI recommends applicable or not_applicable, show the badge
  if (
    status === 'to_review' &&
    recommendedApplicability &&
    (recommendedApplicability === 'applicable' ||
      recommendedApplicability === 'not_applicable')
  ) {
    const recLabel =
      STATUS_LABELS[recommendedApplicability] ?? recommendedApplicability;
    const RECOMMENDATION_ICONS: Record<string, FC<{ className?: string }>> = {
      applicable: ApplicableIcon,
      not_applicable: NotApplicableIcon,
    };
    const RecIcon = RECOMMENDATION_ICONS[recommendedApplicability];
    if (!RecIcon) return null;

    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-lg text-xs font-medium tracking-[0.18px] bg-neutral-50 border border-dashed border-neutral-200">
        <RecIcon className="h-3.5 w-3.5" />
        <span className="text-neutral-900">
          {recLabel}{' '}
          <span className="italic font-normal text-neutral-500">
            (Recommended)
          </span>
        </span>
      </span>
    );
  }

  // No badge when there's no applicable/not_applicable recommendation
  return null;
};

export const UpdateCard: FC<{
  update: ListUpdatesResponse;
  isNew?: boolean;
  onClick: () => void;
  onStatusChange?: (status: ApplicabilityStatus) => void;
}> = ({ update, isNew, onClick, onStatusChange }) => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const status = update.context?.status;
  const effectiveStatus = status ?? 'to_review';
  const statusTooltipLabel = STATUS_LABELS[effectiveStatus] ?? effectiveStatus;

  const publicationDate = formatRelativeDate(update.update.publicationDate);
  const effectiveDate = formatRelativeDate(update.update.effectiveDate);

  const handleStatusClick = (e: MouseEvent, newStatus: ApplicabilityStatus) => {
    e.stopPropagation();
    onStatusChange?.(newStatus);
  };

  const source = update.reference?.source ?? update.source;
  const referenceTitle =
    update.kind === 'news'
      ? (update.source?.title ?? update.update.shortSummary ?? '-')
      : (update.reference?.title ?? update.update.shortSummary ?? '-');

  const hasSummaryOrStatusSection =
    !!update.update.summary || !!update.context?.status;

  return (
    <button
      className="group relative w-full max-w-[800px] min-w-[800px] bg-white border border-neutral-200 rounded-2xl shadow-md p-4 text-left transition-all hover:shadow-lg hover:border-neutral-300 cursor-pointer"
      onClick={onClick}
    >
      {isNew && (
        <div
          className={cn(
            'absolute -top-1 -left-1 z-10 h-3 w-3 rounded-full ring-2 ring-white shadow-[0_2px_4px_0_rgba(0,0,0,0.15)]',
            effectiveStatus === 'out_of_scope'
              ? 'bg-neutral-400'
              : 'bg-yellow-400'
          )}
        />
      )}

      <FlexCol gap="2.5">
        {/* Section 1: Title + Dates */}
        <FlexCol gap="1.5">
          {/* Title row: status icon + summary text + update type badge + priority */}
          <FlexRow gap="2.5" alignItems="center" className="min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="shrink-0">
                  <StatusIcon status={effectiveStatus} />
                </div>
              </TooltipTrigger>
              <TooltipContent>{statusTooltipLabel}</TooltipContent>
            </Tooltip>
            <p className="flex-1 min-w-0 text-sm font-medium text-neutral-900 leading-snug line-clamp-2">
              {update.update.shortSummary ??
                update.update.summary ??
                referenceTitle}
            </p>
            <FlexRow gap="2" alignItems="center" className="shrink-0">
              {update.update.updateType && (
                <UpdateTypeBadge updateType={update.update.updateType} />
              )}
              <PriorityDisplay
                priority={update.context?.priority ?? null}
                aiImpactStatus={update.context?.aiImpactStatus ?? null}
              />
            </FlexRow>
          </FlexRow>

          {/* Dates row */}
          <FlexRow
            gap="3"
            alignItems="center"
            className="text-xs text-neutral-500"
          >
            {publicationDate && (
              <span>
                Published:{' '}
                <span className="font-medium text-neutral-700">
                  {publicationDate.relative}
                </span>{' '}
                ({publicationDate.absolute})
              </span>
            )}
            {effectiveDate && (
              <span>
                Effective:{' '}
                <span className="font-medium text-neutral-700">
                  {effectiveDate.relative}
                </span>{' '}
                ({effectiveDate.absolute})
              </span>
            )}
          </FlexRow>
        </FlexCol>

        <Separator />

        {/* Section 2: Document */}
        <FlexCol gap="1.5">
          <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-500">
            {update.kind === 'news' ? 'relevant news' : 'document'}
          </span>

          <NewsfeedRelevantChipsRow>
            {workspaceUname ? (
              <DocumentReferenceButton
                workspaceUname={workspaceUname}
                referenceId={
                  update.kind === 'news' ? undefined : update.reference?.id
                }
                title={referenceTitle}
              />
            ) : (
              <span
                className={cn(
                  DOCUMENT_REFERENCE_BUTTON_CLASS,
                  'cursor-default'
                )}
              >
                <FileText className="h-3 w-3 shrink-0 text-neutral-900" />
                <Ellipsis lineClamp={1} hasTooltip className="min-w-0">
                  {referenceTitle}
                </Ellipsis>
              </span>
            )}

            <DocumentTypeBadge
              type={
                update.kind === 'news' ? 'news' : (update.reference?.type ?? '')
              }
            />
            {source && (
              <RegulatoryRegionBadge
                geography={source.geography}
                institutionGeographies={source.institutionGeographies}
                institutionName={source.institutionName}
              />
            )}
            {source && (
              <SourceBadge
                sourceTitle={source.title}
                sourceIcon={getSourceIconUrl(source.id, source.icon)}
              />
            )}
          </NewsfeedRelevantChipsRow>
        </FlexCol>

        {/* Section 3: Update summary (newsfeed preview — not impact assessment) */}
        {hasSummaryOrStatusSection && (
          <>
            <Separator />

            <div className="relative">
              <FlexCol gap="1.5">
                {/* Header row: overline + status badge */}
                <FlexRow
                  alignItems="center"
                  justifyContent="between"
                  className="min-w-0"
                >
                  <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-500">
                    update summary
                  </span>
                  {status === 'to_review' && (
                    <ImpactStatusBadge
                      status={status}
                      aiImpactStatus={update.context?.aiImpactStatus ?? null}
                      recommendedApplicability={
                        update.context?.recommendedApplicability ?? null
                      }
                    />
                  )}
                </FlexRow>

                {update.update.summary && (
                  <div className="min-w-0">
                    <MarkdownText
                      variant="updateCardPreview"
                      className="gap-0 [&_div]:mt-0 [&_p]:mt-0 [&_ul]:my-0 [&_ol]:my-0 [&_li]:mb-0"
                    >
                      {update.update.summary}
                    </MarkdownText>
                  </div>
                )}
              </FlexCol>

              {/* Hover status pill */}
              {onStatusChange && (
                <div className="absolute right-0 top-0 hidden group-hover:flex items-center gap-1 rounded-xl bg-neutral-200 p-1">
                  {STATUS_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const isActive = status === opt.value;
                    const isRecommended =
                      status === 'to_review' &&
                      update.context?.recommendedApplicability === opt.value;
                    const PILL_ICONS: Record<
                      string,
                      FC<{ className?: string }>
                    > = {
                      applicable: ApplicableIcon,
                      not_applicable: NotApplicableIcon,
                    };
                    const RECOMMENDATION_BG: Record<string, string> = {
                      applicable: 'bg-green-50 hover:bg-green-100',
                      not_applicable: 'bg-red-50 hover:bg-red-100',
                    };
                    const FilledIcon = PILL_ICONS[opt.value];

                    if (isRecommended && FilledIcon) {
                      return (
                        <div
                          key={opt.value}
                          role="button"
                          onClick={e => handleStatusClick(e, opt.value)}
                          className={cn(
                            'flex items-center gap-2 min-h-[36px] px-4 py-[7.5px] rounded-lg border border-black shadow-sm transition-colors cursor-pointer',
                            RECOMMENDATION_BG[opt.value]
                          )}
                        >
                          <FilledIcon className="h-5 w-5" />
                          <div className="flex flex-col items-start leading-tight">
                            <span className="text-sm font-medium text-neutral-900 tracking-[0.07px]">
                              {STATUS_LABELS[opt.value]}
                            </span>
                            <span className="text-xs italic text-neutral-500">
                              Recommended
                            </span>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={opt.value}
                        role="button"
                        onClick={e => handleStatusClick(e, opt.value)}
                        className={cn(
                          'flex items-center justify-center self-stretch min-w-[32px] rounded-[10px] px-[10px] py-[5.5px] transition-colors',
                          isActive
                            ? 'bg-neutral-200 border border-neutral-300 cursor-default'
                            : 'bg-white shadow-sm hover:bg-neutral-100 cursor-pointer'
                        )}
                      >
                        <Icon className="h-5 w-5 text-neutral-900" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </FlexCol>
    </button>
  );
};
